// BEGIN FILE: src/components/VimshottariDashaTimeline.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Box, Button, IconButton, Input, Typography, Tooltip, Chip } from '@mui/joy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  classifyFunctionalForLagna,
  getGrahaLongitudesAndMotionSidereal,
  getAscendantSignAndNakshatra,
  getWholeSignCuspsSidereal,
  assignPlanetsToHouses,
  getRahuKetuSidereal
} from '../utils/astroEngine';
import { getAyanamsaProvider, type AyanamsaProvider } from '../utils/ayanamsa';
import { computeHouseScoresWithLordsAndAspects, type AspectEdge } from '../utils/houseScores';
import { supabase } from '../utils/supabaseClient';

/* =========================
   Types & constants
   ========================= */

type DashaPlanet =
  | 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';
type SevenPlanet = Exclude<DashaPlanet, 'Rahu'|'Ketu'>;
type FuncMap = Record<SevenPlanet, 'benefic'|'malefic'|'neutral'>;

type DashaBand = { lord: DashaPlanet; start: string; end: string };

type Props = {
  isoDate: string;
  latitude: number;
  longitude: number;
  ascSign: number; // 1..12
  ayanamsa?: AyanamsaProvider;
  blendedPct?: Partial<Record<DashaPlanet, number>>; // optional: planet blended % (signed, -100..+100)
  externalHouseScores?: Record<number, number> | null; // optional: use external house scores instead of computing internally
  externalAspects?: Array<{ from: string; toHouse: number }> | null; // optional: use external aspects instead of computing
  showFocusTable?: boolean; // optional: show Dasha Focus Overview table (default true)
  showOnlyFocusTable?: boolean; // optional: show ONLY the focus table, hide timeline and controls (default false)
  compact?: boolean; // optional: compact layout for grid columns (default false)
  // External date control (if provided, internal state is ignored)
  center?: DateTime;
  onCenterChange?: (dt: DateTime) => void;
  yearsSpan?: number;
  onYearsSpanChange?: (span: number) => void;
  hideControls?: boolean; // optional: hide date picker and navigation controls
  onAnalyseCurrentTime?: () => void;
  isAdmin?: boolean;
  remainingAnalyses?: number;
  audioGuide?: React.ReactNode;
};

/** Vimśottarī canonical order & year lengths */
type DashaLord = 'Ketu'|'Venus'|'Sun'|'Moon'|'Mars'|'Rahu'|'Jupiter'|'Saturn'|'Mercury';
const DASHA_ORDER: DashaLord[] = ['Ketu','Venus','Sun','Moon','Mars','Rahu','Jupiter','Saturn','Mercury'];
const DASHA_YEARS: Record<DashaLord, number> = {
  Ketu:7, Venus:20, Sun:6, Moon:10, Mars:7, Rahu:18, Jupiter:16, Saturn:19, Mercury:17
};
const TOTAL_YEARS = 120;

/** Dasha year length (days)
 * Many mainstream calculators use the sidereal year (~365.25636 days).
 * Adjust to 360 if you want the nakshatra year convention instead.
 */
const DASHA_YEAR_DAYS = 365.25636;
const DAY_MS = 86400000;
const YEAR_MS = DASHA_YEAR_DAYS * DAY_MS;

const NAK_LEN_DEG = 360 / 27; // 13°20'
const SEVEN: SevenPlanet[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'];
const isSeven = (p: DashaPlanet): p is SevenPlanet => (SEVEN as string[]).includes(p);

const NAK_LORDS: DashaLord[] = Array.from({ length: 27 }, (_, i) => DASHA_ORDER[i % 9]);

// House rulership by sign
const SIGN_LORD: Record<number, DashaPlanet> = {
  1:'Mars',2:'Venus',3:'Mercury',4:'Moon',5:'Sun',6:'Mercury',
  7:'Venus',8:'Mars',9:'Jupiter',10:'Saturn',11:'Saturn',12:'Jupiter',
};

// Parāśari special aspects (houses away)
const PARASHARI_OFFSETS: Record<DashaPlanet, number[]> = {
  Sun:[7], Moon:[7], Mercury:[7], Venus:[7],
  Mars:[4,7,8], Jupiter:[5,7,9], Saturn:[3,7,10],
  Rahu:[5,7,9], Ketu:[5,7,9]
};

const HOUSE_AREAS: Record<number, string> = {
  1:'Self, Vitality, Body',
  2:'Wealth, Family, Speech',
  3:'Courage, Siblings, Effort',
  4:'Home, Mother, Comforts',
  5:'Children, Creativity, Intellect',
  6:'Enemies, Debts, Health',
  7:'Marriage, Partnerships, Public',
  8:'Longevity, Transformations, Secrets',
  9:'Fortune, Dharma, Higher learning',
 10:'Career, Status, Authority',
 11:'Gains, Networks, Aspirations',
 12:'Losses, Expenditure, Moksha',
};

/* =========================
   Vimshottari helpers
   ========================= */

const toMs = (x: string | Date | number) =>
  typeof x === 'number' ? x : (typeof x === 'string' ? new Date(x).getTime() : (x as Date).getTime());
const fromMs = (ms: number) => new Date(ms).toISOString();

function mergeAdjacentSameLord(bands: DashaBand[]): DashaBand[] {
  const out: DashaBand[] = [];
  for (const b of bands) {
    const last = out[out.length - 1];
    if (last && last.lord === b.lord) {
      const endMs = Math.max(toMs(last.end), toMs(b.end));
      last.end = fromMs(endMs);
    } else {
      out.push({ ...b });
    }
  }
  return out;
}

function pickCurrent(bands: DashaBand[] | undefined, whenMs: number): DashaBand | null {
  if (!bands || bands.length === 0) return null;
  for (const b of bands) {
    const s = toMs(b.start), e = toMs(b.end);
    if (whenMs >= s && whenMs < e) return b;
  }
  return bands.reduce(
    (acc, b) => (toMs(b.start) <= whenMs && toMs(b.end) > (acc ? toMs(acc.end) : -Infinity) ? b : acc),
    bands[0]
  );
}

function buildSubdasha(parent: DashaBand): DashaBand[] {
  const lord = parent.lord as DashaLord;
  const startIdx = DASHA_ORDER.indexOf(lord);
  const spanMs = Math.max(1, toMs(parent.end) - toMs(parent.start));
  let t = toMs(parent.start);
  const out: DashaBand[] = [];
  for (let k = 0; k < 9; k++) {
    const sub = DASHA_ORDER[(startIdx + k) % 9];
    const frac = DASHA_YEARS[sub] / TOTAL_YEARS;
    const dur = spanMs * frac;
    const s = t;
    const e = t + dur;
    out.push({ lord: sub as DashaPlanet, start: fromMs(s), end: fromMs(e) });
    t = e;
  }
  return mergeAdjacentSameLord(out);
}

/** Compute MD timeline accurately from Moon’s sidereal longitude (at birth) */
function computeMahadashaTimelineAround(
  birthISO: string,
  moonLonSiderealDeg: number,
  center: DateTime,
  yearsEachSide: number
): DashaBand[] {
  const birthMs = DateTime.fromISO(birthISO).toMillis();
  const viewStartMs = center.minus({ years: yearsEachSide }).toMillis();
  const viewEndMs = center.plus({ years: yearsEachSide }).toMillis();

  const lon = ((moonLonSiderealDeg % 360) + 360) % 360;
  const nakIndex = Math.floor(lon / NAK_LEN_DEG); // 0..26
  const nakStartLon = nakIndex * NAK_LEN_DEG;
  const posInNak = lon - nakStartLon;
  const fracElapsed = posInNak / NAK_LEN_DEG;

  const curLord = NAK_LORDS[nakIndex];
  const curYears = DASHA_YEARS[curLord];

  // Current MD started before birth by the elapsed fraction
  const elapsedMs = curYears * fracElapsed * YEAR_MS;
  const curMdStartMs = birthMs - elapsedMs;

  // Forward
  const forward: DashaBand[] = [];
  let idx = DASHA_ORDER.indexOf(curLord);
  let t = curMdStartMs;
  while (t < viewEndMs + YEAR_MS * 2) {
    const lord = DASHA_ORDER[idx % 9];
    const dur = DASHA_YEARS[lord] * YEAR_MS;
    const s = t, e = t + dur;
    forward.push({ lord: lord as DashaPlanet, start: fromMs(s), end: fromMs(e) });
    t = e;
    idx++;
  }

  // Backward
  const backward: DashaBand[] = [];
  let tend = curMdStartMs;
  let backIdx = (DASHA_ORDER.indexOf(curLord) - 1 + 9) % 9;
  while (tend > viewStartMs - YEAR_MS * 2) {
    const lord = DASHA_ORDER[backIdx % 9];
    const dur = DASHA_YEARS[lord] * YEAR_MS;
    const s = tend - dur;
    backward.push({ lord: lord as DashaPlanet, start: fromMs(s), end: fromMs(tend) });
    tend = s;
    backIdx = (backIdx - 1 + 9) % 9;
  }

  const all = mergeAdjacentSameLord([...backward.reverse(), ...forward]);
  const margin = YEAR_MS * 0.05;
  return all.filter(b => toMs(b.end) >= (viewStartMs - margin) && toMs(b.start) <= (viewEndMs + margin));
}

/* =========================
   UI helpers
   ========================= */

function colorFor(lord: DashaBand['lord'], funcMap: FuncMap) {
  if (isSeven(lord)) {
    const role = funcMap[lord];
    if (role === 'benefic') return '#21a366';
    if (role === 'malefic') return '#c9302c';
    return '#d4b106';
  }
  return '#d4b106';
}
const fmt = (d: string) => {
  const date = new Date(d);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};
const fmtEndInclusive = (d: string) => {
  const date = new Date(new Date(d).getTime() - 1);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Inclusive calendar duration helper (years, months, days)
function humanYmdDuration(startISO: string, endISO: string): string {
  try {
    let s = DateTime.fromISO(startISO).startOf('day');
    const e = DateTime.fromISO(endISO).minus({ days: 1 }).endOf('day');
    if (e < s) return '—';
    const y = Math.floor(e.diff(s, 'years').years ?? 0);
    s = s.plus({ years: y });
    const m = Math.floor(e.diff(s, 'months').months ?? 0);
    s = s.plus({ months: m });
    const d = Math.floor(e.diff(s, 'days').days ?? 0);
    return `${y}y ${m}m ${d}d`;
  } catch {
    return '—';
  }
}

/** Format blended % for display */
const fmtBlended = (planet: DashaPlanet, blendedPct?: Partial<Record<DashaPlanet, number>>): string => {
  if (!blendedPct) return '';
  const val = blendedPct[planet];
  if (val == null || !Number.isFinite(val)) return '';
  const sign = val >= 0 ? '+' : '';
  return ` (${sign}${Math.round(val * 10) / 10}%)`;
};

/* =========================
   Component - Memoized for performance
   ========================= */

function VimshottariDashaTimeline({ 
  isoDate, latitude, longitude, ascSign, ayanamsa, blendedPct, externalHouseScores, externalAspects,
  showFocusTable = true, showOnlyFocusTable = false, compact = false,
  center: externalCenter, onCenterChange, yearsSpan: externalYearsSpan, onYearsSpanChange, hideControls = false,
  onAnalyseCurrentTime, isAdmin, remainingAnalyses, audioGuide
}: Props) {
  const [internalCenter, setInternalCenter] = useState<DateTime>(() => DateTime.now());
  const [internalYearsSpan, setInternalYearsSpan] = useState<number>(12); // +/- years around center
  
  // Use external or internal state
  const center = externalCenter ?? internalCenter;
  const setCenter = onCenterChange ?? setInternalCenter;
  const yearsSpan = externalYearsSpan ?? internalYearsSpan;
  const setYearsSpan = onYearsSpanChange ?? setInternalYearsSpan;
  
  const scrollerRef = useRef<HTMLDivElement|null>(null);
  const trackRef = useRef<HTMLDivElement|null>(null);

  // Hover overlay (throttled via rAF)
  const [hoverMs, setHoverMs] = useState<number | null>(null);
  const [hoverX, setHoverX] = useState<number | null>(null);
  const rafRef = useRef<number | null>(null);

  // House data from Supabase
  const [housesData, setHousesData] = useState<Record<number, { relations?: string; body_parts?: string; professions?: string }>>({});
  const [housesLoadState, setHousesLoadState] = useState<{ loading: boolean; error?: string; count: number }>({ loading: false, count: 0 });
  const [housesReloadKey, setHousesReloadKey] = useState(0);

  // Fetch house data on mount
  useEffect(() => {
    const fetchHouses = async () => {
      setHousesLoadState({ loading: true, count: 0 });
      try {
        // If Supabase isn't configured, skip the network call and surface a helpful hint.
        const envInfo: any = (typeof window !== 'undefined' ? (window as any).__SUPABASE_ENV : undefined);
        const hasUrl = Boolean(envInfo?.SUPABASE_URL || (process as any)?.env?.REACT_APP_SUPABASE_URL);
        const hasKey = Boolean(envInfo?.SUPABASE_ANON_KEY_SET || (process as any)?.env?.REACT_APP_SUPABASE_ANON_KEY);
        if (!hasUrl || !hasKey) {
          setHousesLoadState({ loading: false, count: 0, error: 'Supabase env not configured' });
          return;
        }

        const { data, error } = await supabase
          .from('houses')
          .select('id, relations, body_parts, professions')
          .order('id', { ascending: true });
        if (error) throw error;
        if (data && Array.isArray(data)) {
          const map: Record<number, { relations?: string; body_parts?: string; professions?: string }> = {};
          data.forEach((h: any) => {
            if (typeof h?.id === 'number') {
              map[h.id] = { relations: h.relations, body_parts: h.body_parts, professions: h.professions };
            }
          });
          setHousesData(map);
          setHousesLoadState({ loading: false, count: Object.keys(map).length });
        } else {
          setHousesLoadState({ loading: false, count: 0, error: 'No data returned' });
        }
      } catch (err: any) {
        console.error('[VimDasha] Failed to fetch house data:', err);
        const msg = err?.message || String(err);
        setHousesLoadState({ loading: false, count: 0, error: msg });
      }
    };
    fetchHouses();
  }, [housesReloadKey]);

  // Moon sidereal longitude at birth
  const moonLon = useMemo(() => {
    try {
      const g = getGrahaLongitudesAndMotionSidereal(
        { isoDate, latitude, longitude },
        ayanamsa ?? getAyanamsaProvider('lahiri')
      ) as any;
      return (g?.Moon?.lon ?? 0) as number;
    } catch {
      return 0;
    }
  }, [isoDate, latitude, longitude, ayanamsa]);

  // Functional map for colours
  const funcMap = useMemo<FuncMap>(() => {
    const m = classifyFunctionalForLagna(ascSign) as Record<string, 'benefic'|'malefic'|'neutral'>;
    const out = {} as FuncMap;
    SEVEN.forEach(p => { out[p] = m[p] ?? 'neutral'; });
    return out;
  }, [ascSign]);

  // MD across window
  const md = useMemo(() => computeMahadashaTimelineAround(isoDate, moonLon, center, yearsSpan),
    [isoDate, moonLon, center, yearsSpan]);

  // AD/PD across window
  const adAll = useMemo(() => mergeAdjacentSameLord(md.flatMap(b => buildSubdasha(b))), [md]);
  const pdAll = useMemo(() => mergeAdjacentSameLord(adAll.flatMap(b => buildSubdasha(b))), [adAll]);

  // Center snapshot (just for row titles)
  const { mdAtCenter, adAtCenter } = useMemo(() => {
    const nowMs = center.toMillis();
    const mdCur = pickCurrent(md, nowMs) || md[0] || null;
    const adCur = mdCur ? pickCurrent(buildSubdasha(mdCur), nowMs) : null;
    return { mdAtCenter: mdCur, adAtCenter: adCur };
  }, [md, center]);

  // Today snapshot chips
  const todaySnap = useMemo(() => {
    const t = Date.now();
    const mdNow = pickCurrent(md, t);
    const adNow = mdNow ? pickCurrent(buildSubdasha(mdNow), t) : null;
    const pdNow = adNow ? pickCurrent(buildSubdasha(adNow), t) : null;
    return { mdNow, adNow, pdNow };
  }, [md]);

  // Timeline sizing
  const PX_PER_DAY = 1.2;
  const domainStart = center.minus({ years: yearsSpan }).toMillis();
  const domainEnd = center.plus({ years: yearsSpan }).toMillis();
  const domainSpanDays = Math.max(1, Math.round((domainEnd - domainStart) / DAY_MS));
  const trackWidth = Math.max(600, Math.round(domainSpanDays * PX_PER_DAY)); // px

  const xPos = (dateISOorMs: string | number) => {
    const t = typeof dateISOorMs === 'number' ? dateISOorMs : new Date(dateISOorMs).getTime();
    return ((t - domainStart) / (domainEnd - domainStart)) * trackWidth;
  };
  const xToMs = (x: number) => {
    const r = Math.min(1, Math.max(0, x / trackWidth));
    return domainStart + r * (domainEnd - domainStart);
  };

  // Auto-center scroller on the cursor
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const cx = xPos(center.toMillis());
    const scrollTo = Math.max(0, cx - el.clientWidth / 2);
    try { el.scrollTo({ left: scrollTo, behavior: 'auto' }); } catch { el.scrollLeft = scrollTo; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, trackWidth]);

  // Jump helpers
  const jump = (delta: { years?: number; months?: number }) => {
    if (onCenterChange) {
      onCenterChange(center.plus(delta));
    } else {
      setInternalCenter(c => c.plus(delta));
    }
  };
  const jumpToday = () => {
    if (onCenterChange) {
      onCenterChange(DateTime.now());
    } else {
      setInternalCenter(DateTime.now());
    }
  };

  // Hover handlers (smoothed with rAF)
  const onMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const clampedX = Math.max(0, Math.min(trackWidth, x));

    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setHoverX(clampedX);
      setHoverMs(xToMs(clampedX));
    });
  };
  const onMouseLeave = () => { setHoverMs(null); setHoverX(null); };

  // Hover snapshot (from hoverMs)
  const hoverSnap = useMemo(() => {
    if (hoverMs == null) return null;
    const mdH = pickCurrent(md, hoverMs);
    const adH = mdH ? pickCurrent(buildSubdasha(mdH), hoverMs) : null;
    const pdH = adH ? pickCurrent(buildSubdasha(adH), hoverMs) : null;
    return { mdH, adH, pdH };
  }, [hoverMs, md]);

  // Compute PD and SD at center as well
  const pdAtCenter = useMemo(() => {
    if (!mdAtCenter) return null;
    const ad = buildSubdasha(mdAtCenter);
    const nowMs = center.toMillis();
    const curAd = pickCurrent(ad, nowMs);
    return curAd ? pickCurrent(buildSubdasha(curAd), nowMs) : null;
  }, [mdAtCenter, center]);

  const sdAtCenter = useMemo(() => {
    if (!mdAtCenter) return null;
    const ad = buildSubdasha(mdAtCenter);
    const nowMs = center.toMillis();
    const curAd = pickCurrent(ad, nowMs);
    if (!curAd) return null;
    const pd = buildSubdasha(curAd);
    const curPd = pickCurrent(pd, nowMs);
    return curPd ? pickCurrent(buildSubdasha(curPd), nowMs) : null;
  }, [mdAtCenter, center]);

  // Natal D1 placements (occupancy basis)
  const natal = useMemo(() => {
    try {
      const provider = ayanamsa ?? getAyanamsaProvider('lahiri');
      const g = getGrahaLongitudesAndMotionSidereal({ isoDate, latitude, longitude }, provider) as any;
      const nodes = getRahuKetuSidereal({ isoDate, latitude, longitude }, provider) as any;
      const longs: Partial<Record<DashaPlanet, number>> = {
        Sun: g?.Sun?.lon, Moon: g?.Moon?.lon, Mars: g?.Mars?.lon, Mercury: g?.Mercury?.lon,
        Jupiter: g?.Jupiter?.lon, Venus: g?.Venus?.lon, Saturn: g?.Saturn?.lon,
        Rahu: nodes?.Rahu, Ketu: nodes?.Ketu,
      };
      const asc = getAscendantSignAndNakshatra(isoDate, latitude, longitude);
      const cusps = getWholeSignCuspsSidereal(asc.siderealLongitudeDeg);
      const placements = assignPlanetsToHouses(cusps, longs as any) as Record<DashaPlanet, { house: number }>;
      return { asc, cusps, placements };
    } catch {
      return null;
    }
  }, [isoDate, latitude, longitude, ayanamsa]);

  // Transit snapshot at center date-time
  const transit = useMemo(() => {
    if (!natal?.cusps) return null;
    try {
      const iso = center.toISO();
      const provider = ayanamsa ?? getAyanamsaProvider('lahiri');
      const g = getGrahaLongitudesAndMotionSidereal({ isoDate: iso!, latitude, longitude }, provider) as any;
  const nodes = getRahuKetuSidereal({ isoDate: iso!, latitude, longitude }, provider) as any;
      const longs: Partial<Record<DashaPlanet, number>> = {
        Sun: g?.Sun?.lon, Moon: g?.Moon?.lon, Mars: g?.Mars?.lon, Mercury: g?.Mercury?.lon,
        Jupiter: g?.Jupiter?.lon, Venus: g?.Venus?.lon, Saturn: g?.Saturn?.lon,
        Rahu: nodes?.Rahu, Ketu: nodes?.Ketu,
      };
      // Use NATAL cusps to calculate which natal houses transiting planets occupy
  const placements = assignPlanetsToHouses(natal.cusps, longs as any) as Record<DashaPlanet, { house: number }>;
  return { placements, longs };
    } catch {
      return null;
    }
  }, [center, latitude, longitude, ayanamsa, natal]);

  const housesRuledBy = (planet: DashaPlanet): number[] => {
    const out: number[] = [];
    // Use D1 natal lagna strictly for house indexing
    const ascSignD1 = (() => {
      const ascObj = natal?.asc;
      const byIndex = (ascObj as any)?.ascendantSignIndex;
      if (typeof byIndex === 'number' && Number.isFinite(byIndex)) return ((Math.round(byIndex) - 1 + 12*10) % 12) + 1;
      const lon = (ascObj as any)?.siderealLongitudeDeg;
      if (typeof lon === 'number' && Number.isFinite(lon)) return Math.floor((((lon % 360)+360)%360)/30) + 1;
      // Fallback to prop ascSign if natal is unavailable
      return ((Math.round(ascSign) - 1 + 12*10) % 12) + 1;
    })();
    for (let s = 1; s <= 12; s++) {
      if (SIGN_LORD[s] === planet) {
        const h = ((s - ascSignD1 + 12) % 12) + 1;
        out.push(h);
      }
    }
    return out;
  };

  const aspectsFromHouse = (planet: DashaPlanet, house?: number): number[] => {
    if (!house) return [];
    const offs = PARASHARI_OFFSETS[planet] ?? [7];
    return offs.map(off => ((house - 1 + (off - 1)) % 12) + 1);
  };

  const houseLabel = (h: number) => `H${h}${HOUSE_AREAS[h] ? ` (${HOUSE_AREAS[h]})` : ''}`;

  // Build natal Parashara aspects as edges (house-based)
  const natalAspectEdges = useMemo<AspectEdge[] | null>(() => {
    // If external aspects provided, use them
    if (externalAspects && Array.isArray(externalAspects)) {
      return externalAspects.map(a => ({ from: a.from as DashaPlanet, toHouse: a.toHouse }));
    }
    
    // Otherwise compute from natal placements
    if (!natal?.placements) return null;
    const edges: AspectEdge[] = [];
    (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as DashaPlanet[])
      .forEach((p) => {
        const h = natal.placements?.[p]?.house;
        if (!h) return;
        (PARASHARI_OFFSETS[p] ?? [7]).forEach((off) => {
          const to = ((h - 1 + (off - 1)) % 12) + 1;
          edges.push({ from: p as any, toHouse: to });
        });
      });
    return edges;
  }, [externalAspects, natal]);

  // House scores across the chart using blended % and natal placements/aspects
  const houseScores = useMemo<number[] | null>(() => {
    // If external house scores provided, use them (convert Record<number, number> to number[])
    if (externalHouseScores) {
      const scores = Array(12).fill(0);
      for (let h = 1; h <= 12; h++) {
        scores[h - 1] = externalHouseScores[h] ?? 0;
      }
      return scores;
    }
    
    // Otherwise compute internally
    if (!natal?.placements || !natal?.asc || !natalAspectEdges) return null;
    // Build planetPct defaulting to 0
    const planetPct: Required<Record<DashaPlanet, number>> = {
      Sun: 0, Moon: 0, Mars: 0, Mercury: 0, Jupiter: 0, Venus: 0, Saturn: 0, Rahu: 0, Ketu: 0,
      ...(blendedPct as any || {})
    } as any;
    const ascIdx = (natal.asc as any)?.ascendantSignIndex || ascSign;
    try {
      return computeHouseScoresWithLordsAndAspects(planetPct as any, natal.placements as any, natalAspectEdges as any, ascIdx, { clampTo: 999 });
    } catch {
      return null;
    }
  }, [externalHouseScores, natal, blendedPct, natalAspectEdges, ascSign]);

  const labelWithScore = (h: number) => {
    const base = houseLabel(h);
    const sc = houseScores && Number.isFinite(houseScores[h-1]) ? Math.round((houseScores[h-1]) * 10) / 10 : null;
    return sc != null ? `${base} [${sc}]` : base;
  };

  const focusRow = (tag: 'MD'|'AD'|'PD'|'SD', band: DashaBand | null) => {
    if (!band) return null;
    const p = band.lord;
    const ruled = housesRuledBy(p);
    const occNatal = natal?.placements?.[p]?.house;
    const aspsAll = aspectsFromHouse(p, occNatal);
    // Show all aspects (not just to empty houses)
    const occupiedSet = new Set<number>(
      Object.values(natal?.placements ?? {}).map((v:any) => v?.house).filter((h:any)=>typeof h==='number') as number[]
    );
    const aspEmpty = aspsAll; // Show all aspects, not filtered by occupancy
    // Focus houses are natal ruled + natal occupied
    const focusHouses = new Set<number>([...ruled, ...(occNatal ? [occNatal] : [])]);
    const transitingInFocus: DashaPlanet[] = [];
    // Planets transiting in the dasha planet's occupied house
    const transitingInOccupiedHouse: DashaPlanet[] = [];
    // Compute transit houses in natal frame when possible
    const tHousesNatal: Record<DashaPlanet, { house: number }> | null =
      (natal?.cusps && transit?.longs)
        ? assignPlanetsToHouses(natal.cusps, transit.longs as any) as any
        : (transit?.placements ?? null);
    (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as DashaPlanet[]).forEach(q => {
      const h = tHousesNatal?.[q]?.house;
      if (h && focusHouses.has(h)) transitingInFocus.push(q);
      // Check if transiting in the dasha planet's occupied house
      if (h && occNatal && h === occNatal) transitingInOccupiedHouse.push(q);
    });
    
    // Find companion planets: those in same house OR mutually aspecting the dasha planet
    const companions: DashaPlanet[] = [];
    if (natal?.placements) {
      (['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as DashaPlanet[]).forEach(q => {
        if (q === p) return; // skip self
        const qHouse = natal.placements?.[q]?.house;
        // Same house?
        if (qHouse && occNatal && qHouse === occNatal) {
          companions.push(q);
          return;
        }
        // Mutual aspect?
        const pAspects = aspectsFromHouse(p, occNatal);
        const qAspects = aspectsFromHouse(q, qHouse);
        const pAspectsQ = qHouse && pAspects.includes(qHouse);
        const qAspectsP = occNatal && qAspects.includes(occNatal);
        if (pAspectsQ && qAspectsP) companions.push(q);
      });
    }
    
    // Get house data for occupied house
    const houseInfo = occNatal ? housesData[occNatal] : null;
    const occDetails: string[] = [];
    if (occNatal) {
      occDetails.push(labelWithScore(occNatal)); // Use labelWithScore to include house score
      if (houseInfo?.relations) occDetails.push(`Relations: ${houseInfo.relations}`);
      if (houseInfo?.body_parts) occDetails.push(`Body: ${houseInfo.body_parts}`);
      if (houseInfo?.professions) occDetails.push(`Career: ${houseInfo.professions}`);
    }
  const occStr = occDetails.length ? occDetails.join(' • ') : '—';
    
  const ruledStr = ruled.length ? ruled.map(labelWithScore).join(', ') : '—';
  const aspStr = aspEmpty.length ? aspEmpty.map(labelWithScore).join(', ') : '—';
    const start = DateTime.fromISO(band.start).toFormat('dd/LL/yyyy');
    const end = fmtEndInclusive(band.end);
    const duration = humanYmdDuration(band.start, band.end);
    const transitingWhere = tHousesNatal?.[p]?.house ? houseLabel(tHousesNatal![p]!.house) : '—';
    const whoInOccHouse = transitingInOccupiedHouse.length 
      ? transitingInOccupiedHouse.join(', ') 
      : (occNatal ? `No planet transiting in ${houseLabel(occNatal)}` : '—');
    const focusSet = new Set<number>([...ruled, ...(occNatal?[occNatal]:[]), ...aspEmpty]);
    return { tag, planet: p, companions, start, end, duration, ruledStr, occStr, aspStr, transitingWhere, whoInOccHouse, focusHouses: Array.from(focusSet), ruled, occNatal: occNatal || 0, aspEmpty } as any;
  };

  // Memoize table rows so they recompute when housesData changes
  const tableRows = useMemo(() => {
    return [
      focusRow('MD', mdAtCenter),
      focusRow('AD', adAtCenter),
      focusRow('PD', pdAtCenter),
      focusRow('SD', sdAtCenter),
    ].filter(Boolean) as Array<ReturnType<typeof focusRow>>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mdAtCenter, adAtCenter, pdAtCenter, sdAtCenter, natal, transit, housesData, houseScores]);

  // If showOnlyFocusTable is true, render ONLY the focus table
  if (showOnlyFocusTable) {
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        {showFocusTable !== false && (
          <Box sx={{ mt: 1, p: 1, borderRadius: 'md', border: '1px solid var(--joy-palette-neutral-outlinedBorder)' }}>
            <Typography level="title-sm" sx={{ mb: 0.5, fontWeight: 600 }}>
              Dasha Focus Overview
            </Typography>
            {/* House meta status */}
            {housesLoadState.loading && (
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Loading house meta…</div>
            )}
            {!housesLoadState.loading && housesLoadState.count < 12 && (
              <div style={{ fontSize: 12, color: '#8a6d3b', marginBottom: 6, display:'flex', alignItems:'center', gap:8 }}>
                <span>
                  House meta unavailable{housesLoadState.error ? ` — ${housesLoadState.error}` : ''}. Ensure Supabase env is set and anon read is allowed for the houses table.
                </span>
                <Button size="sm" variant="outlined" onClick={() => setHousesReloadKey(k => k + 1)}>Retry</Button>
              </div>
            )}
            <Box sx={{ 
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'var(--joy-palette-neutral-100)',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--joy-palette-neutral-400)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'var(--joy-palette-neutral-500)',
              },
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760, fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign:'left', padding:'8px 10px', borderBottom:'1px solid #ddd' }}>Level • Planet</th>
                    <th style={{ textAlign:'left', padding:'8px 10px', borderBottom:'1px solid #ddd' }}>From → To</th>
                    <th style={{ textAlign:'left', padding:'8px 10px', borderBottom:'1px solid #ddd' }}>Rules • Occupies • Aspects (life areas)</th>
                    <th style={{ textAlign:'left', padding:'8px 10px', borderBottom:'1px solid #ddd' }}>Dasha Planet Transit (today)</th>
                    <th style={{ textAlign:'left', padding:'8px 10px', borderBottom:'1px solid #ddd' }}>Planets transiting in dasha planet's occupied house</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    if (!tableRows.length) return (
                      <tr><td colSpan={5} style={{ padding:'8px 10px' }}>No dasha data.</td></tr>
                    );
                    const freq: Record<number, number> = {};
                    tableRows.forEach((rr:any) => {
                      (rr.ruled || []).forEach((h:number) => { freq[h] = (freq[h]||0) + 1; });
                      if (rr.occNatal) freq[rr.occNatal] = (freq[rr.occNatal]||0) + 1;
                      (rr.aspEmpty || []).forEach((h:number) => { freq[h] = (freq[h]||0) + 1; });
                    });
                    
                    const renderList = (hs:number[]) => (
                      hs.map((h, i) => (
                        <span key={`h-${h}-${i}`} style={{ fontWeight: (freq[h]||0) >= 2 ? 700 : 400 }}>
                          {labelWithScore(h)}{i < hs.length - 1 ? ', ' : ''}
                        </span>
                      ))
                    );
                    
                    const renderOccupies = (r: any) => {
                      if (r.occStr === '—') return '—';
                      const isBold = (freq[r.occNatal]||0) >= 2;
                      const parts = r.occStr.split(' • ');
                      if (parts.length === 0) return r.occStr;
                      const houseLabel = parts[0];
                      const metadata = parts.slice(1).join(' • ');
                      return (
                        <>
                          <span style={{ fontWeight: isBold ? 700 : 400 }}>{houseLabel}</span>
                          {metadata && <span> • {metadata}</span>}
                        </>
                      );
                    };

                    return tableRows.map((r:any, idx:number) => (
                      <tr key={`focus-${idx}`}>
                        <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom: r!.companions.length > 0 ? 4 : 0 }}>
                            <strong>{r!.tag}</strong>:
                            <Chip size="sm" variant="solid" sx={{ bgcolor: colorFor(r!.planet, funcMap), color: '#fff', px: 0.75, py: 0 }}>
                              {r!.planet}
                            </Chip>
                            {blendedPct && (blendedPct as any)[r!.planet as any] != null && (
                              <span style={{ fontSize: 11, color: '#666' }}>
                                {fmtBlended(r!.planet as any, blendedPct as any)}
                              </span>
                            )}
                          </div>
                          {r!.companions.length > 0 && (
                            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:4, paddingLeft:12 }}>
                              {r!.companions.map((comp: DashaPlanet) => (
                                <div key={comp} style={{ display:'flex', alignItems:'center', gap:4 }}>
                                  <span style={{ color: colorFor(comp, funcMap), fontWeight: 600, fontSize: 12 }}>
                                    {comp}
                                  </span>
                                  {blendedPct && (blendedPct as any)[comp as any] != null && (
                                    <span style={{ fontSize: 10, color: '#888' }}>
                                      {fmtBlended(comp as any, blendedPct as any)}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                          {r!.start} → {r!.end}
                          <div style={{ fontSize: 12, color: '#666' }}>Duration: {r!.duration}</div>
                        </td>
                        <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                          <div><strong>Rules:</strong> {r!.ruled && r!.ruled.length > 0 ? renderList(r.ruled) : '—'}</div>
                          <div><strong>Occupies:</strong> {renderOccupies(r)}</div>
                          <div><strong>Aspects:</strong> {r!.aspEmpty && r!.aspEmpty.length > 0 ? renderList(r.aspEmpty) : '—'}</div>
                        </td>
                        <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                          {r!.transitingWhere}
                        </td>
                        <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                          {r!.whoInOccHouse}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </Box>
            <div style={{ fontSize: 12, color:'#666', marginTop: 6 }}>
              Notes: "Transit today" follows the date in the timeline's picker. Focus houses = houses owned by MD/AD/PD plus the house currently occupied.
            </div>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      {/* Controls */}
      {!hideControls && (
        <>
          <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:0.5 }}>
            <Typography level="h4" sx={{ fontWeight: 700, fontFamily: '"Cinzel", "Garamond", serif', color: '#b83050' }}>Vimśottarī Timeline</Typography>
            {audioGuide && <Box sx={{ flexShrink: 0 }}>{audioGuide}</Box>}
          </Box>
          <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1, flexWrap:'nowrap' }}>
            <Tooltip title="Back one year">
              <IconButton size="sm" variant="outlined" onClick={() => jump({ years: -1 })}>−Y</IconButton>
            </Tooltip>
            <Tooltip title="Back one month">
              <IconButton size="sm" variant="outlined" onClick={() => jump({ months: -1 })}>−M</IconButton>
            </Tooltip>
            <Button size="sm" variant="soft" onClick={jumpToday}>Today</Button>
            <Tooltip title="Forward one month">
              <IconButton size="sm" variant="outlined" onClick={() => jump({ months: +1 })}>+M</IconButton>
            </Tooltip>
            <Tooltip title="Forward one year">
              <IconButton size="sm" variant="outlined" onClick={() => jump({ years: +1 })}>+Y</IconButton>
            </Tooltip>
            <Input
              size="sm"
              type="date"
              value={center.toISODate()!}
              onChange={(e) => {
                const v = e.target.value;
                if (v) {
                  const newCenter = DateTime.fromISO(v);
                  if (onCenterChange) {
                    onCenterChange(newCenter);
                  } else {
                    setInternalCenter(newCenter);
                  }
                }
              }}
              sx={{ width: 180, ml:1 }}
            />
          </Box>
        </>
      )}

      {/* Heading with selected date's dasha chips - Outside timeline box */}
      <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography level="title-md" sx={{ fontWeight: 700, fontSize: '1rem', fontFamily: '"Cinzel", "Garamond", serif', color: '#7a1a2e' }}>
          Vimshottari Dasha Sequence
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'center' }}>
          {mdAtCenter && <Chip size="sm" variant="soft" sx={{ bgcolor: colorFor(mdAtCenter.lord, funcMap), color:'#111', fontSize: '0.75rem' }}>
            MD: {mdAtCenter.lord}{fmtBlended(mdAtCenter.lord, blendedPct)}
          </Chip>}
          {adAtCenter && <Chip size="sm" variant="soft" sx={{ bgcolor: colorFor(adAtCenter.lord, funcMap), color:'#111', fontSize: '0.75rem' }}>
            AD: {adAtCenter.lord}{fmtBlended(adAtCenter.lord, blendedPct)}
          </Chip>}
          {pdAtCenter && <Chip size="sm" variant="soft" sx={{ bgcolor: colorFor(pdAtCenter.lord, funcMap), color:'#111', fontSize: '0.75rem' }}>
            PD: {pdAtCenter.lord}{fmtBlended(pdAtCenter.lord, blendedPct)}
          </Chip>}
          {sdAtCenter && <Chip size="sm" variant="soft" sx={{ bgcolor: colorFor(sdAtCenter.lord, funcMap), color:'#111', fontSize: '0.75rem' }}>
            SD: {sdAtCenter.lord}{fmtBlended(sdAtCenter.lord, blendedPct)}
          </Chip>}
        </Box>
      </Box>

      {/* Timeline tracks */}
      <Box
        ref={scrollerRef}
        sx={{
          overflowX:'auto',
          overflowY:'hidden',
          border:'1px solid var(--joy-palette-neutral-outlinedBorder)',
          borderRadius:'md',
          bgcolor:'neutral.softBg'
        }}
      >
        <Box
          ref={trackRef}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          sx={{ minWidth: trackWidth, position:'relative', p:1 }}
        >
          {/* Cursor & Today lines */}
          {/* Selected date cursor - shows the date selected in controls */}
          <Box
            sx={{
              position:'absolute', top:0, bottom:0,
              left: `${xPos(center.toMillis())}px`,
              width: 1, 
              borderLeft: '2px dashed',
              borderColor: 'primary.500',
              opacity: 0.5, 
              pointerEvents:'none', 
              zIndex: 2
            }}
          >
            <Typography 
              level="body-xs" 
              sx={{ 
                position: 'absolute', 
                top: -20, 
                left: 2, 
                fontSize: '9px',
                color: 'primary.600',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                textShadow: '0 0 2px white'
              }}
            >
              Selected: {center.toISODate()}
            </Typography>
          </Box>
          
          {/* Today marker - shows current date */}
          {Date.now() >= domainStart && Date.now() <= domainEnd && (
            <Box
              sx={{
                position:'absolute', top:0, bottom:0,
                left: `${xPos(Date.now())}px`,
                width: 3, 
                bgcolor:'warning.solidBg', 
                opacity: 0.8, 
                pointerEvents:'none', 
                zIndex: 3,
                boxShadow: '0 0 4px rgba(255,152,0,0.5)'
              }}
            >
              <Typography 
                level="body-xs" 
                sx={{ 
                  position: 'absolute', 
                  top: -20, 
                  left: 4, 
                  fontSize: '9px',
                  color: 'warning.700',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  textShadow: '0 0 2px white'
                }}
              >
                TODAY
              </Typography>
            </Box>
          )}
          {/* Hover line */}
          {hoverX != null && (
            <Box
              sx={{
                position:'absolute', top:0, bottom:0,
                left: `${hoverX}px`, width: 1,
                bgcolor:'primary.solidBg', opacity: 0.6, pointerEvents:'none', zIndex: 2
              }}
            />
          )}

          {/* Rows (stacked, no absolute Y, tight spacing) */}
          <Row
            title={`Mahadasha (MD)${mdAtCenter ? ` in ${mdAtCenter.lord}${fmtBlended(mdAtCenter.lord, blendedPct)}` : ''}`}
            titleSize="body-xs"
            height={30}
            bands={md}
            color={(lord) => colorFor(lord, funcMap)}
            xPos={xPos}
          />
          <Row
            title={`Antardasha (AD)${mdAtCenter ? ` in ${mdAtCenter.lord}${fmtBlended(mdAtCenter.lord, blendedPct)}` : ''}`}
            titleSize="body-xs"
            height={26}
            bands={adAll}
            color={(lord) => colorFor(lord, funcMap)}
            xPos={xPos}
          />
          <Row
            title={`Pratyantardasha (PD)${adAtCenter ? ` in ${adAtCenter.lord}${fmtBlended(adAtCenter.lord, blendedPct)}` : ''}`}
            titleSize="body-xs"
            height={22}
            bands={pdAll}
            color={(lord) => colorFor(lord, funcMap)}
            xPos={xPos}
          />

          {/* Hover tag with MD/AD/PD at pointer */}
          {hoverX != null && hoverSnap && hoverMs && (
            <Box
              sx={{
                position:'absolute',
                top: 6,
                left: Math.max(6, Math.min(trackWidth - 320, hoverX - 160)),
                px: 0.75, py: 0.5,
                borderRadius: 'sm',
                bgcolor: 'background.surface',
                boxShadow: 'sm',
                border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                fontSize: 12,
                zIndex: 3
              }}
            >
              {/* Date display */}
              <Typography level="body-xs" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'center', mb: 0.25 }}>
                {DateTime.fromMillis(hoverMs).toFormat('dd MMM yyyy')}
              </Typography>
              
              {/* Dasha chips */}
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                {hoverSnap.mdH && (
                  <Chip size="sm" variant="soft" sx={{ bgcolor: colorFor(hoverSnap.mdH.lord, funcMap), color:'#111' }}>
                    MD: {hoverSnap.mdH.lord}{fmtBlended(hoverSnap.mdH.lord, blendedPct)}
                  </Chip>
                )}
                {hoverSnap.adH && (
                  <Chip size="sm" variant="soft" sx={{ bgcolor: colorFor(hoverSnap.adH.lord, funcMap), color:'#111' }}>
                    AD: {hoverSnap.adH.lord}{fmtBlended(hoverSnap.adH.lord, blendedPct)}
                  </Chip>
                )}
                {hoverSnap.pdH && (
                  <Chip size="sm" variant="soft" sx={{ bgcolor: colorFor(hoverSnap.pdH.lord, funcMap), color:'#111' }}>
                    PD: {hoverSnap.pdH.lord}{fmtBlended(hoverSnap.pdH.lord, blendedPct)}
                  </Chip>
                )}
                {/* Sukshma Dasha on hover */}
                {(() => {
                  let sdH: DashaBand | null = null;
                  if (hoverSnap.pdH) {
                    const sdBands = buildSubdasha(hoverSnap.pdH);
                    sdH = pickCurrent(sdBands, hoverMs!);
                  }
                  return sdH ? (
                    <Chip size="sm" variant="soft" sx={{ bgcolor: colorFor(sdH.lord, funcMap), color:'#111' }}>
                      SD: {sdH.lord}{fmtBlended(sdH.lord, blendedPct)}
                    </Chip>
                  ) : null;
                })()}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* SD chip below timeline, centered */}
      {sdAtCenter && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
          <Chip
            size="sm"
            variant="soft"
            sx={{
              bgcolor: colorFor(sdAtCenter.lord, funcMap),
              color: '#111',
              fontWeight: 600,
              fontSize: 13,
              px: 1.5,
              py: 0.4,
              borderRadius: 1.5,
              boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
              letterSpacing: 0.1,
              height: 26,
            }}
          >
            <span style={{ fontWeight: 700 }}>Sukshma Dasha:</span> {sdAtCenter.lord}{fmtBlended(sdAtCenter.lord, blendedPct)}
            <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 8 }}>
              {DateTime.fromISO(sdAtCenter.start).toFormat('dd/LL/yyyy')} – {fmtEndInclusive(sdAtCenter.end)}
            </span>
          </Chip>
        </Box>
      )}

      {/* Legend */}
      <Box sx={{ mt:1, display:'flex', gap:2, flexWrap:'wrap', fontSize:12, color:'text.secondary' }}>

      {/* Focus table under timeline */}
      {showFocusTable && (
  <Box sx={{ mt: 1, p: 1, borderRadius: 'md', border: '1px solid var(--joy-palette-neutral-outlinedBorder)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography level="title-sm" sx={{ fontWeight: 600 }}>
            Dasha Focus Overview
          </Typography>
          <Tooltip 
            title="Detailed breakdown of current planetary periods (MD/AD/PD/SD). Shows which houses each dasha planet rules, occupies, and aspects - revealing which life areas are activated now. Transit column shows current sky positions reinforcing or challenging these themes." 
            placement="top" 
            arrow 
            variant="soft" 
            size="sm"
            sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem', lineHeight: 1.5 }}
          >
            <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 20, minHeight: 20, padding: '2px' }}>
              <HelpOutlineIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
        {/* House meta status (visible only if not fully loaded) */}
        {housesLoadState.loading && (
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Loading house meta…</div>
        )}
        {!housesLoadState.loading && housesLoadState.count < 12 && (
          <div style={{ fontSize: 12, color: '#8a6d3b', marginBottom: 6, display:'flex', alignItems:'center', gap:8 }}>
            <span>
              House meta unavailable{housesLoadState.error ? ` — ${housesLoadState.error}` : ''}. Ensure Supabase env is set and anon read is allowed for the houses table.
            </span>
            <Button size="sm" variant="outlined" onClick={() => setHousesReloadKey(k => k + 1)}>Retry</Button>
          </div>
        )}
        <Box sx={{ 
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'var(--joy-palette-neutral-100)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--joy-palette-neutral-400)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'var(--joy-palette-neutral-500)',
          },
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 550, fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd', minWidth: '140px', verticalAlign: 'top' }}>Level • Planet • Period</th>
                <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>Rules • Occupies • Aspects</th>
                <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd', minWidth: '90px', maxWidth: '110px', wordWrap: 'break-word' }}>Transiting in Occupied House</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                if (!tableRows.length) return (
                  <tr><td colSpan={3} style={{ padding:'8px 10px' }}>No dasha data.</td></tr>
                );
                // Build frequency of ALL houses (ruled, occupied, aspected) across rows to highlight repeats
                const freq: Record<number, number> = {};
                tableRows.forEach((rr:any) => {
                  (rr.ruled || []).forEach((h:number) => { freq[h] = (freq[h]||0) + 1; });
                  if (rr.occNatal) freq[rr.occNatal] = (freq[rr.occNatal]||0) + 1;
                  (rr.aspEmpty || []).forEach((h:number) => { freq[h] = (freq[h]||0) + 1; });
                });
                
                const renderList = (hs:number[]) => (
                  hs.map((h, i) => (
                    <span key={`h-${h}-${i}`} style={{ fontWeight: (freq[h]||0) >= 2 ? 700 : 400 }}>
                      {labelWithScore(h)}{i < hs.length - 1 ? ', ' : ''}
                    </span>
                  ))
                );
                
                // Helper to render Occupies with score and bold if common, preserving metadata
                const renderOccupies = (r: any) => {
                  if (r.occStr === '—') return '—';
                  const isBold = (freq[r.occNatal]||0) >= 2;
                  // Split occStr: first part is "H{n} (life areas) [score]", rest is metadata
                  const parts = r.occStr.split(' • ');
                  if (parts.length === 0) return r.occStr;
                  const houseLabel = parts[0]; // e.g., "H4 (Home, Mother, Comforts) [-53.0]"
                  const metadata = parts.slice(1).join(' • '); // e.g., "Relations: ... • Body: ... • Career: ..."
                  return (
                    <>
                      <span style={{ fontWeight: isBold ? 700 : 400 }}>{houseLabel}</span>
                      {metadata && <span> • {metadata}</span>}
                    </>
                  );
                };

                return tableRows.map((r:any, idx:number) => (
                  <tr key={`focus-${idx}`}>
                    <td style={{ padding:'5px 8px', borderBottom:'1px solid #eee', verticalAlign: 'top' }}>
                      {/* Level and Planet */}
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom: 4 }}>
                        <strong style={{ fontSize: 11 }}>{r!.tag}</strong>:
                        <Chip size="sm" variant="solid" sx={{ bgcolor: colorFor(r!.planet, funcMap), color: '#fff', px: 0.6, py: 0, fontSize: 11 }}>
                          {r!.planet}
                        </Chip>
                        {blendedPct && (blendedPct as any)[r!.planet as any] != null && (
                          <span style={{ fontSize: 10, color: '#666' }}>
                            {fmtBlended(r!.planet as any, blendedPct as any)}
                          </span>
                        )}
                      </div>
                      
                      {/* Companions */}
                      {r!.companions.length > 0 && (
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:4, paddingLeft:8 }}>
                          {r!.companions.map((comp: DashaPlanet) => (
                            <div key={comp} style={{ display:'flex', alignItems:'center', gap:3 }}>
                              <span style={{ color: colorFor(comp, funcMap), fontWeight: 600, fontSize: 11 }}>
                                {comp}
                              </span>
                              {blendedPct && (blendedPct as any)[comp as any] != null && (
                                <span style={{ fontSize: 9, color: '#888' }}>
                                  {fmtBlended(comp as any, blendedPct as any)}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Period dates and duration */}
                      <div style={{ fontSize: 10, color: '#555', marginBottom: 3 }}>
                        <div>{r!.start} → {r!.end}</div>
                        <div style={{ color: '#888' }}>({r!.duration})</div>
                      </div>
                      
                      {/* Transit info */}
                      <div style={{ fontSize: 10, color: '#666', paddingTop: 3, borderTop: '1px solid #f0f0f0' }}>
                        <strong>Transit:</strong> {r!.transitingWhere}
                      </div>
                    </td>
                    
                    <td style={{ padding:'5px 8px', borderBottom:'1px solid #eee', verticalAlign: 'top', fontSize: 11 }}>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Rules:</strong> {r!.ruled && r!.ruled.length > 0 ? renderList(r.ruled) : '—'}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Occupies:</strong> {renderOccupies(r)}
                      </div>
                      <div>
                        <strong>Aspects:</strong> {r!.aspEmpty && r!.aspEmpty.length > 0 ? renderList(r.aspEmpty) : '—'}
                      </div>
                    </td>
                    
                    <td style={{ padding:'5px 8px', borderBottom:'1px solid #eee', verticalAlign: 'top', fontSize: 11, wordWrap: 'break-word', maxWidth: '110px' }}>
                      {r!.whoInOccHouse}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </Box>
        <div style={{ fontSize: 12, color:'#666', marginTop: 6 }}>
          Notes: “Transit today” follows the date in the timeline’s picker. Focus houses = houses owned by MD/AD/PD plus the house currently occupied.
        </div>
      </Box>
        )}        <Box><span style={{ display:'inline-block', width:12, height:12, background:'#21a366', borderRadius:2, marginRight:6 }} /> Functional Benefic</Box>
        <Box><span style={{ display:'inline-block', width:12, height:12, background:'#d4b106', borderRadius:2, marginRight:6 }} /> Neutral / Nodes</Box>
        <Box><span style={{ display:'inline-block', width:12, height:12, background:'#c9302c', borderRadius:2, marginRight:6 }} /> Functional Malefic</Box>
        <Box sx={{ ml:'auto' }}>
          <span style={{ color:'var(--joy-palette-warning-700)' }}>Today</span> &nbsp;|&nbsp; <span>Cursor</span> &nbsp;|&nbsp; <span style={{ color:'var(--joy-palette-primary-700)' }}>Hover</span>
        </Box>
      </Box>

      {/* Optional span control */}
      {!hideControls && (
        <Box sx={{ mt:1, display:'flex', alignItems:'center', gap:1 }}>
          <Typography level="body-sm" sx={{ opacity: 0.7 }}>Span:</Typography>
          <Button size="sm" variant="outlined" onClick={() => {
            const newSpan = Math.max(3, yearsSpan - 3);
            if (onYearsSpanChange) {
              onYearsSpanChange(newSpan);
            } else {
              setInternalYearsSpan(newSpan);
            }
          }}>−3y</Button>
          <Typography level="body-sm">{yearsSpan * 2} years</Typography>
          <Button size="sm" variant="outlined" onClick={() => {
            const newSpan = Math.min(60, yearsSpan + 3);
            if (onYearsSpanChange) {
              onYearsSpanChange(newSpan);
            } else {
              setInternalYearsSpan(newSpan);
            }
          }}>+3y</Button>
        </Box>
      )}
    </Box>
  );
}

/* =========================
   Row: stacked track
   ========================= */

function Row({
  title, titleSize, height, bands, color, xPos
}:{
  title: string;
  titleSize?: 'body-xs' | 'body-sm';
  height: number;
  bands: DashaBand[];
  color: (lord: DashaPlanet) => string;
  xPos: (dateISOorMs: string | number) => number;
}) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography level={titleSize || "body-sm"} sx={{ mb: 0.5, opacity: 0.8 }}>
        {title}
      </Typography>
      <Box
        sx={{
          position:'relative',
          height,
          border:'1px solid #e5e7eb',
          borderRadius: 8,
          overflow:'hidden',
          bgcolor:'#fafafa'
        }}
      >
        {bands.map((b, i) => {
          const x1 = xPos(b.start), x2 = xPos(b.end);
          const w = Math.max(1, Math.round(x2 - x1));
          return (
            <Box
              key={`${b.lord}-${i}-${b.start}`}
              title={`${b.lord} - ${fmt(b.start)} -> ${fmtEndInclusive(b.end)}`}
              sx={{
                position:'absolute',
                top:0,
                left: `${x1}px`,
                width: `${w}px`,
                height:'100%',
                bgcolor: color(b.lord),
                display:'flex',
                alignItems:'center',
                justifyContent:'center',
                fontSize:12,
                color:'#111',
                borderRight:'1px solid rgba(0,0,0,0.08)',
                whiteSpace:'nowrap',
                overflow:'hidden',
                textOverflow:'ellipsis'
              }}
            >
              <span style={{ padding:'0 6px', fontWeight: 700 }}>{b.lord}</span>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Export memoized version to prevent unnecessary re-renders
export default React.memo(VimshottariDashaTimeline);
export {};
// END FILE: src/components/VimshottariDashaTimeline.tsx
